import * as LocalAuthentication from 'expo-local-authentication';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';
import { Session } from '@supabase/supabase-js';

const BIOMETRIC_ENABLED_KEY = 'biometric_enabled';
const BIOMETRIC_SESSION_KEY = 'biometric_session';
export const BIOMETRIC_SKIP_ONCE_KEY = 'biometric_skip_once';

const isAvailable = async (): Promise<boolean> => {
  if (Platform.OS === 'web') return false;
  try {
    const hasHardware = await LocalAuthentication.hasHardwareAsync();
    if (!hasHardware) return false;
    const isEnrolled = await LocalAuthentication.isEnrolledAsync();
    return isEnrolled;
  } catch {
    return false;
  }
};

const getSupportedTypes = async (): Promise<LocalAuthentication.AuthenticationType[]> => {
  try {
    return await LocalAuthentication.supportedAuthenticationTypesAsync();
  } catch {
    return [];
  }
};

const hasFaceId = async (): Promise<boolean> => {
  if (Platform.OS !== 'ios') return false;
  try {
    const types = await getSupportedTypes();
    return types.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION);
  } catch {
    return false;
  }
};

const authenticate = async (reason: string): Promise<boolean> => {
  // PRD §5.1 — `auth_biometric_prompt_shown` micro event. Fires before the
  // native authenticate dialog so the dashboard sees prompt volume vs the
  // success rate (`auth_login_succeeded` with `method=biometric`).
  import('@/utils/clarityEvents')
    .then(({ logAuthBiometricPromptShown }) => logAuthBiometricPromptShown())
    .catch(() => {});
  try {
    const result = await LocalAuthentication.authenticateAsync({
      promptMessage: reason,
      cancelLabel: 'Usar senha',
      fallbackLabel: 'Usar senha',
      disableDeviceFallback: false,
    });
    return result.success;
  } catch {
    return false;
  }
};

const storeSession = async (session: Session): Promise<void> => {
  const value = JSON.stringify({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });
  await SecureStore.setItemAsync(BIOMETRIC_SESSION_KEY, value);
  await SecureStore.setItemAsync(BIOMETRIC_ENABLED_KEY, 'true');
};

const updateStoredSession = async (accessToken: string, refreshToken: string): Promise<void> => {
  try {
    const enabled = await SecureStore.getItemAsync(BIOMETRIC_ENABLED_KEY);
    if (enabled !== 'true') return;
    const value = JSON.stringify({ access_token: accessToken, refresh_token: refreshToken });
    await SecureStore.setItemAsync(BIOMETRIC_SESSION_KEY, value);
  } catch {
    // ignore
  }
};

const getStoredSession = async (): Promise<Pick<Session, 'access_token' | 'refresh_token'> | null> => {
  try {
    const value = await SecureStore.getItemAsync(BIOMETRIC_SESSION_KEY);
    if (!value) return null;
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const isEnabled = async (): Promise<boolean> => {
  if (Platform.OS === 'web') return false;
  try {
    const value = await SecureStore.getItemAsync(BIOMETRIC_ENABLED_KEY);
    return value === 'true';
  } catch {
    return false;
  }
};

const disable = async (): Promise<void> => {
  try {
    await SecureStore.deleteItemAsync(BIOMETRIC_SESSION_KEY);
    await SecureStore.deleteItemAsync(BIOMETRIC_ENABLED_KEY);
  } catch {
    // ignore
  }
};

export const biometricService = {
  isAvailable,
  hasFaceId,
  authenticate,
  storeSession,
  updateStoredSession,
  getStoredSession,
  isEnabled,
  disable,
};
